import React, { useState, useEffect } from 'react';
import { useAdminApiService } from '../../hooks/useAdminApiService';
import './AdminComponents.css';

// Types
interface AdminAccount {
  id: string;
  accountNumber: string;
  customerName: string;
  customerEmail: string;
  type: string;
  status: string;
  balance: number;
  currency: string;
  createdAt: string;
  lastActivity: string;
  parentAccountId?: string;
}

interface AccountGroup {
  customerName: string;
  customerEmail: string;
  accounts: AdminAccount[];
  childAccounts: AdminAccount[];
  totalBalance: number;
  currencies: Set<string>;
}

const AccountManagement: React.FC = () => {
  const [accounts, setAccounts] = useState<AdminAccount[]>([]);
  const [groupedAccounts, setGroupedAccounts] = useState<AccountGroup[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedChildSections, setExpandedChildSections] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const { getAllAccounts } = useAdminApiService();

  useEffect(() => {
    const loadAccounts = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getAllAccounts(currentPage, pageSize);
        if (response.success && response.data && response.data.content) {
          console.log('Raw API response:', response.data.content);
          
          const mappedAccounts = response.data.content.map((acc: any) => {
            console.log('Raw account from API:', acc);
            const account = {
              id: acc.id?.toString() ?? '',
              accountNumber: acc.accountNumber ?? acc.account_number ?? '',
              customerName: acc.customerName ?? acc.customer ?? '',
              customerEmail: acc.customerEmail ?? acc.customer_email ?? '',
              type: acc.accountType ?? acc.type ?? '',
              status: acc.status ?? '',
              balance: Number(acc.balance ?? 0),
              currency: acc.currency ?? '',
              createdAt: acc.createdAt ?? acc.created_at ?? '',
              lastActivity: acc.lastTransactionAt ?? acc.last_activity ?? '',
              parentAccountId: acc.parentAccountId ?? acc.parent_account_id ?? null,
            };
            console.log('Mapped account:', account);
            console.log('parentAccountId type:', typeof account.parentAccountId, 'value:', account.parentAccountId);
            return account;
          });

          console.log('All mapped accounts:', mappedAccounts);

          // Update pagination state
          setTotalPages(response.data.totalPages || 1);
          
          // Group parent and child accounts together
          groupAccounts(mappedAccounts);
        } else {
          setError(response.error || 'Failed to load accounts');
        }
      } catch (err) {
        console.error('Error loading accounts:', err);
        setError('Failed to load accounts');
      } finally {
        setLoading(false);
      }
    };

    loadAccounts();
  }, [currentPage, pageSize]); // Removed getAllAccounts from dependencies to prevent duplicate calls

  const groupAccounts = (accounts: AdminAccount[]) => {
    console.log('Starting groupAccounts with:', accounts);
    
    const accountMap = new Map<string, AdminAccount>();
    const groups = new Map<string, AccountGroup>();

    // Map all accounts by id for easy lookup
    accounts.forEach(account => {
      accountMap.set(account.id, account);
    });

    console.log('Account map:', Array.from(accountMap.entries()));

    // First, create groups for parent accounts
    accounts.forEach(account => {
      console.log(`Processing account ${account.id}: parentAccountId = ${account.parentAccountId}`);
      
      if (!account.parentAccountId || account.parentAccountId === 'null' || account.parentAccountId === '') {
        console.log(`Account ${account.id} is a parent account`);
        // This is a parent account
        if (!groups.has(account.customerName)) {
          groups.set(account.customerName, {
            customerName: account.customerName,
            customerEmail: account.customerEmail,
            accounts: [],
            childAccounts: [],
            totalBalance: 0,
            currencies: new Set(),
          });
        }
        const group = groups.get(account.customerName)!;
        group.accounts.push(account);
        group.totalBalance += account.balance;
        group.currencies.add(account.currency);
      }
    });

    // Now, add child accounts to the correct parent group
    accounts.forEach(account => {
      if (account.parentAccountId && account.parentAccountId !== 'null' && account.parentAccountId !== '') {
        console.log(`Account ${account.id} is a child account with parentAccountId: ${account.parentAccountId}`);
        // Find the parent account - try both string and number matching
        let parentAccount = accountMap.get(account.parentAccountId);
        if (!parentAccount) {
          // Try converting parentAccountId to number if it's a string
          const numericParentId = parseInt(account.parentAccountId);
          if (!isNaN(numericParentId)) {
            parentAccount = accountMap.get(numericParentId.toString());
          }
        }
        console.log(`Found parent account:`, parentAccount);
        
        if (parentAccount) {
          // Find the group for the parent account's customer
          const group = groups.get(parentAccount.customerName);
          console.log(`Found group for ${parentAccount.customerName}:`, group);
          
          if (group) {
            group.childAccounts.push(account);
            group.totalBalance += account.balance;
            group.currencies.add(account.currency);
            console.log(`Added child account ${account.id} to group ${parentAccount.customerName}`);
          }
        } else {
          console.log(`WARNING: Could not find parent account with ID: ${account.parentAccountId}`);
        }
      }
    });

    const groupedArray = Array.from(groups.values());
    console.log('Final grouped accounts:', groupedArray);
    setGroupedAccounts(groupedArray);
  };

  const toggleGroup = (customerName: string) => {
    const newExpandedGroups = new Set(expandedGroups);
    if (expandedGroups.has(customerName)) {
      newExpandedGroups.delete(customerName);
      // When collapsing a group, also collapse its child section
      setExpandedChildSections(prev => {
        const newSet = new Set(prev);
        newSet.delete(customerName);
        return newSet;
      });
    } else {
      newExpandedGroups.add(customerName);
      // When expanding a group, also expand its child section if there are child accounts
      const group = groupedAccounts.find(g => g.customerName === customerName);
      if (group && group.childAccounts.length > 0) {
        setExpandedChildSections(prev => {
          const newSet = new Set(prev);
          newSet.add(customerName);
          return newSet;
        });
      }
    }
    setExpandedGroups(newExpandedGroups);
  };

  const toggleChildSection = (customerName: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering parent group toggle
    const newExpandedChildSections = new Set(expandedChildSections);
    if (expandedChildSections.has(customerName)) {
      newExpandedChildSections.delete(customerName);
    } else {
      newExpandedChildSections.add(customerName);
    }
    setExpandedChildSections(newExpandedChildSections);
  };

  const formatCurrency = (amount: number, currency: string) => {
    return currency
      ? amount.toLocaleString('en-US', { style: 'currency', currency: currency })
      : amount.toLocaleString();
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) {
      return 'N/A';
    }
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toUpperCase()) {
      case 'ACTIVE': return 'status-badge active';
      case 'FROZEN': return 'status-badge suspended';
      case 'CLOSED': return 'status-badge inactive';
      default: return 'status-badge';
    }
  };

  if (loading) {
    return (
      <div className="admin-component-loading">
        <div className="loading-spinner"></div>
        <p>Loading accounts...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-component-error">
        <div className="error-icon">⚠️</div>
        <h3>Error Loading Accounts</h3>
        <p>{error}</p>
        <button className="retry-btn" onClick={() => setError(null)}>
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="admin-component">
      <div className="component-header">
        <h2>Account Management</h2>
        <p>Manage customer accounts and balances</p>
      </div>

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Account #</th>
              <th>Customer</th>
              <th>Type</th>
              <th>Status</th>
              <th>Balance</th>
              <th>Currency</th>
              <th>Last Activity</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {groupedAccounts.map((group) => (
              <React.Fragment key={group.customerName}>
                {/* Group Header */}
                <tr 
                  className={`account-group-header ${expandedGroups.has(group.customerName) ? 'expanded' : ''}`}
                  onClick={() => toggleGroup(group.customerName)}
                >
                  <td colSpan={8}>
                    <div className="group-header-content">
                      <div className="group-header-left">
                        {expandedGroups.has(group.customerName) ? '🔽' : '▶️'} {group.customerName} ({group.accounts.length} account{group.accounts.length !== 1 ? 's' : ''}{group.childAccounts.length > 0 ? ` + ${group.childAccounts.length} child account${group.childAccounts.length !== 1 ? 's' : ''}` : ''})
                      </div>
                      <div className="group-header-right">
                        Total: {formatCurrency(group.totalBalance, Array.from(group.currencies)[0])}
                      </div>
                    </div>
                  </td>
                </tr>

                {/* Account Details (shown when expanded) */}
                {expandedGroups.has(group.customerName) && (
                  <>
                    {/* Parent Accounts */}
                    {group.accounts.map((account) => (
                      <tr key={account.id} className="account-row parent-account">
                        <td className="indented">{account.accountNumber}</td>
                        <td>{account.customerName}</td>
                        <td>{account.type}</td>
                        <td>
                          <span className={getStatusBadgeClass(account.status)}>
                            {account.status}
                          </span>
                        </td>
                        <td>{formatCurrency(account.balance, account.currency)}</td>
                        <td>{account.currency}</td>
                        <td>{account.lastActivity}</td>
                        <td>{formatDate(account.createdAt)}</td>
                      </tr>
                    ))}

                    {/* Child Accounts Section */}
                    {group.childAccounts.length > 0 && (
                      <>
                        <tr 
                          className={`child-accounts-header ${expandedChildSections.has(group.customerName) ? 'expanded' : ''}`}
                          onClick={(e) => toggleChildSection(group.customerName, e)}
                        >
                          <td colSpan={8}>
                            <div className="child-accounts-label">
                              {expandedChildSections.has(group.customerName) ? '🔽' : '▶️'} Child Accounts ({group.childAccounts.length})
                            </div>
                          </td>
                        </tr>
                        {expandedChildSections.has(group.customerName) && group.childAccounts.map((account) => (
                          <tr key={account.id} className="account-row parent-account">
                            <td className="double-indented">{account.accountNumber}</td>
                            <td>{account.customerName}</td>
                            <td>{account.type}</td>
                            <td>
                              <span className={getStatusBadgeClass(account.status)}>
                                {account.status}
                              </span>
                            </td>
                            <td>{formatCurrency(account.balance, account.currency)}</td>
                            <td>{account.currency}</td>
                            <td>{account.lastActivity}</td>
                            <td>{formatDate(account.createdAt)}</td>
                          </tr>
                        ))}
                      </>
                    )}
                  </>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="pagination" style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: '16px', 
          marginTop: '24px',
          padding: '16px'
        }}>
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 0}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              background: currentPage === 0 ? '#f3f4f6' : '#ffffff',
              color: currentPage === 0 ? '#9ca3af' : '#374151',
              cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            ← Previous
          </button>
          
          <span style={{ fontSize: '14px', color: '#6b7280' }}>
            Page {currentPage + 1} of {totalPages}
          </span>
          
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage >= totalPages - 1}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              background: currentPage >= totalPages - 1 ? '#f3f4f6' : '#ffffff',
              color: currentPage >= totalPages - 1 ? '#9ca3af' : '#374151',
              cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default AccountManagement; 